import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BotTag } from '../../../../../utils/Tags'
import { useTw } from '../../../../../utils/Styles'
import { useRef, useState, useCallback } from 'react'
import { useIntersectionObserver, useSenderInfo } from '../../../../../utils'
import {
  useConversationsQuery,
  ConversationsQuery,
} from './Conversations.generated'

type ItemProps = { conversation: ConversationsQuery['Conversations'][0] }

export function ConversationItem({ conversation }: ItemProps) {
  const tw = useTw()
  const navigate = useNavigate()
  const [t] = useTranslation('common')
  const sender = useSenderInfo(
    conversation.lastItem || { sender: conversation.visitor }
  )

  return (
    <div
      className={tw`p-4 flex hover:bg-neutral-100 cursor-pointer`}
      onClick={() => navigate(`/conversation/${conversation._id}`)}
    >
      <img
        src={sender.logoURL}
        alt={t('avatars.alt', { name: sender.name })}
        className={tw`select-none inline-block h-12 w-12 object-cover flex-shrink-0 rounded-full`}
      />
      <div className={tw`ml-4 flex flex-grow flex-col overflow-hidden`}>
        <div className={tw`flex items-center justify-between truncate`}>
          <p className={tw`font-bold text-gray-900`}>
            {sender.name} {sender?.bot ? <BotTag /> : null}
          </p>
        </div>
        <p className={tw`whitespace-nowrap truncate text-gray-900`}>
          {conversation?.lastItem?.content?.messageString ??
            t('home.conversation.existing.no_content')}
        </p>
      </div>
    </div>
  )
}

export function ConversationList() {
  const tw = useTw()
  const endRef = useRef<HTMLDivElement>()
  const [shouldFetchMore, setFetchMore] = useState(true)
  const { data, loading, fetchMore } = useConversationsQuery({
    fetchPolicy: 'cache-and-network',
  })

  const onNext = useCallback(() => {
    if (!loading && shouldFetchMore && data?.Conversations?.length >= 10) {
      const length = data.Conversations.length
      fetchMore({
        variables: { after: data.Conversations[length - 1]._id },
      }).then(({ data }) => setFetchMore(data?.Conversations?.length === 10))
    }
  }, [loading, data.Conversations, fetchMore, shouldFetchMore, setFetchMore])

  useIntersectionObserver({
    ref: endRef,
    threshold: 0.1,
    callback: (entries) => {
      if (entries[0].isIntersecting) onNext()
    },
  })

  return (
    <div className={tw`flex flex-col h-full overflow-y-scroll`}>
      <div className={tw`divide-y divide-gray-200 border-b border-gray-200`}>
        {data?.Conversations?.map((convo) => (
          <ConversationItem conversation={convo} key={convo._id} />
        ))}
      </div>
      <div
        ref={endRef}
        className={tw`w-full -mt-52 pt-52 pointer-events-none`}
      />
    </div>
  )
}
